import type { Consent } from 'ProdantixSdk/core/types';
import { ANSWER_EVENT, type ConsentState, type ConsentWindow, READY_EVENT, STATE_KEY } from 'ProdantixSdk/web/consent/index';

type ConsentClient = {
  allowStorage(): void;
  isCookieless(): boolean;
  setConsent(consent: Consent): void;
  takeCookielessId(anonymousId: string): void;
};

type Target = ConsentWindow & Pick<Window, 'addEventListener' | 'removeEventListener'>;

const ready = (client: ConsentClient, state: ConsentState): void => {
  client.takeCookielessId(state.anonymousId);
  if (state.consent !== null) client.setConsent(state.consent);
  else if (state.rule === 'notice') client.allowStorage();
};

/** Hands a client the page banner's decisions, whichever loads first: the
 *  day's cookieless id, notice-only storage, and every answer as it comes. */
export const watchConsent = (client: ConsentClient, target: Target): (() => void) => {
  const known = target[STATE_KEY];
  if (known) ready(client, known);
  const onReady = (event: Event): void => {
    const state = (event as CustomEvent<ConsentState>).detail;
    if (state) ready(client, state);
  };
  const onAnswer = (event: Event): void => {
    const consent = (event as CustomEvent<Consent>).detail;
    if (consent) client.setConsent(consent);
  };
  target.addEventListener(READY_EVENT, onReady);
  target.addEventListener(ANSWER_EVENT, onAnswer);
  return () => {
    target.removeEventListener(READY_EVENT, onReady);
    target.removeEventListener(ANSWER_EVENT, onAnswer);
  };
};
